import { Rectangle } from 'recharts';

interface CompositeBarProps {
    x: number;
    y: number;
    width: number;
    height: number;
    fill: string;
    payload: any;
    potentialValue?: number;
    actualValue: number;
    yAxisDomain: [number, number];
    chartHeight: number;
}

export function CompositeBar(props: any) {
    const { x, y, width, height, fill, payload } = props;

    const actualValue = payload.value;
    const potentialValue = payload.potentialValue;
    const isNetImpact = payload.isNet;

    // Log for transport and net impact
    if (payload.name === 'Transport Collaboration' || payload.name === 'Net Impact') {
        console.log(`CompositeBar - ${payload.name}:`, {
            actualValue,
            potentialValue,
            height,
            y,
            isNetImpact,
            willRender: potentialValue !== null && potentialValue !== undefined
        });
    }

    // Check if potential value exists and is valid (but allow 0 and negative values!)
    if (potentialValue === null || potentialValue === undefined) {
        return <Rectangle {...props} />;
    }

    // Check if values are numbers
    if (typeof actualValue !== 'number' || typeof potentialValue !== 'number') {
        return <Rectangle {...props} />;
    }

    // Get potential bar color - use blue for Net Impact to make it stand out
    const getPotentialBarColor = () => {
        if (isNetImpact) {
            return '#4A90A4'; // Teal/blue for Net Impact potential (solid, not transparent)
        }
        return fill; // Same color as main bar for individual items
    };

    // Special case: if actual and potential have different signs (crossing zero)
    const crossesZero = (actualValue < 0 && potentialValue > 0) || (actualValue > 0 && potentialValue < 0);

    if (crossesZero) {
        // For Transport: actualValue = -1, potentialValue = +5
        // The bar goes from -1 (down) and potential should show going up to +5
        console.log(`${payload.name}: CROSSES ZERO - rendering special potential bar`);

        // Calculate where zero is on the chart relative to the actual bar
        const totalSpan = Math.abs(potentialValue - actualValue);
        const actualSpan = Math.abs(actualValue);
        const potentialSpan = Math.abs(potentialValue);

        // Calculate pixel heights based on the actual bar's height
        const pixelsPerUnit = Math.abs(height) / actualSpan;
        const potentialHeight = potentialSpan * pixelsPerUnit;

        // Calculate percentage change
        const percentChange = Math.abs(((potentialValue - actualValue) / actualValue) * 100).toFixed(0);

        // The actual bar is rendered normally (going down from zero for negative)
        // The potential bar should go UP from zero
        const potentialY = y - potentialHeight; // y is at zero line, go up

        return (
            <g>
                {/* Main solid bar (actual value) */}
                <Rectangle
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={fill}
                    radius={[0, 0, 0, 0]}
                />

                {/* Potential extension (crosses zero - goes opposite direction) */}
                <Rectangle
                    x={x}
                    y={potentialY}
                    width={width}
                    height={potentialHeight}
                    fill={actualValue < 0 && potentialValue > 0 ? '#D71920' : getPotentialBarColor()} // Red for bad impact
                    opacity={0.5}
                    radius={[8, 8, 0, 0]}
                />

                {/* Percentage label on potential bar */}
                {potentialHeight > 20 && (
                    <text
                        x={x + width / 2}
                        y={potentialY + potentialHeight / 2}
                        fill="white"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="12"
                        fontWeight="600"
                    >
                        +{percentChange}%
                    </text>
                )}
            </g>
        );
    }

    // Normal case: both values on same side of zero
    // If no potential increase, just render normal bar
    if (Math.abs(potentialValue) <= Math.abs(actualValue)) {
        return <Rectangle {...props} />;
    }

    console.log(`${payload.name}: RENDERING NORMAL POTENTIAL BAR`);

    // Calculate the extension height based on the proportion
    const potentialIncrease = Math.abs(potentialValue) - Math.abs(actualValue);
    const extensionHeight = (potentialIncrease / Math.abs(actualValue)) * Math.abs(height);

    // Calculate percentage increase
    const percentIncrease = ((potentialIncrease / Math.abs(actualValue)) * 100).toFixed(0);

    // For negative values, height is positive (bar extends down from y)
    // For positive values, y is at the top of the bar
    const isNegative = actualValue < 0;
    const isPositive = actualValue > 0;

    const potentialBarColor = getPotentialBarColor();
    const potentialOpacity = isNetImpact ? 1.0 : 0.3; // Full opacity (solid) for Net Impact, 30% for others

    // For Net Impact, remove the offset to connect seamlessly
    const potentialYOffset = isNetImpact ? 0 : (isNegative ? -0.5 : 0.5);
    const potentialHeightAdjust = isNetImpact ? 0 : 0.5;

    return (
        <g>
            {/* Main solid bar */}
            <Rectangle
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fill}
                radius={[0, 0, 0, 0]}
            />

            {/* Potential extension (lighter) - extends from the end of actual bar */}
            <Rectangle
                x={x}
                y={isNegative ? y + height + potentialYOffset : isPositive ? y - extensionHeight - potentialYOffset : y - extensionHeight}
                width={width}
                height={extensionHeight + potentialHeightAdjust}
                fill={potentialBarColor}
                opacity={potentialOpacity}
                radius={isNegative ? [0, 0, 8, 8] : [8, 8, 0, 0]}
            />

            {/* Percentage label on potential extension */}
            {extensionHeight > 20 && (
                <text
                    x={x + width / 2}
                    y={isNegative ? y + height + extensionHeight / 2 : y - extensionHeight / 2}
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="12"
                    fontWeight="600"
                >
                    +{percentIncrease}%
                </text>
            )}
        </g>
    );
}
